import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ShoppingCart, Star, MapPin, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Services', path: '#services', hasDropdown: false },
    { name: 'Reviews', path: '#reviews', hasDropdown: false },
    { name: 'Blog', path: '/blog', hasDropdown: false, isRoute: true },
    { name: 'Portfolio', path: '#portfolio', hasDropdown: false },
  ];

  return (
    <>
      {/* Top Bar */}
      <div className="bg-gradient-to-r from-zinc-900 to-zinc-800 text-white py-2 px-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center text-sm">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 fill-orange-500 text-orange-500" />
              <span className="font-medium">5-Star Rated</span>
            </div>
            <div className="hidden md:flex items-center gap-1">
              <MapPin className="w-4 h-4 text-orange-500" />
              <span>Serving Sacramento & nearby</span>
            </div>
          </div>
          <a href="tel:9162993189" className="flex items-center gap-1 hover:text-orange-500 transition-colors">
            <Phone className="w-4 h-4" />
            <span className="font-medium">(916) 299-3189</span>
          </a>
        </div>
      </div>

      {/* Main Navbar */}
      <nav
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-black/95 backdrop-blur-lg shadow-lg' : 'bg-black'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-36">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 group">
              <img 
                src="https://customer-assets.emergentagent.com/job_boss-shine-2026/artifacts/56v5wwxv_boss%20logo.jpg"
                alt="Boss Detailing Pro Logo"
                className="h-32 w-auto transform group-hover:scale-110 transition-transform duration-300"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navLinks.map((link) => (
                <div key={link.name} className="relative group">
                  {link.isRoute ? (
                    <Link
                      to={link.path}
                      className="text-white hover:text-orange-500 transition-colors duration-300 font-medium"
                    >
                      {link.name}
                    </Link>
                  ) : (
                    <a
                      href={link.path}
                      className="text-white hover:text-orange-500 transition-colors duration-300 font-medium"
                    >
                      {link.name}
                    </a>
                  )}
                  <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-orange-500 group-hover:w-full transition-all duration-300"></div>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex items-center space-x-4">
              <Button
                className="hidden md:inline-flex bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold px-6 py-2 rounded-md transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-orange-500/50"
                onClick={() => window.location.href = '#quote'}
              >
                Book Now
              </Button>
              
              <button className="md:hidden text-white" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-zinc-900 border-t border-zinc-800">
            <div className="px-4 py-4 space-y-4">
              {navLinks.map((link) => (
                link.isRoute ? (
                  <Link
                    key={link.name}
                    to={link.path}
                    className="block text-white hover:text-orange-500 transition-colors py-2 font-medium"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.name}
                  </Link>
                ) : (
                  <a
                    key={link.name}
                    href={link.path}
                    className="block text-white hover:text-orange-500 transition-colors py-2 font-medium"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.name}
                  </a>
                )
              ))}
              <Button
                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold py-3 rounded-md"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  window.location.href = '#quote';
                }}
              >
                Book Now
              </Button>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default Navbar;
